import { FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

export type TForm<T> = { [key in keyof T]: FormControl };

// Should the helper return errors for a field after the form has been submitted or after the field has been touched.
export type ShowErrorsOn = 'submit' | 'touched';

// Maps a key from ValidationErrors to a translation key.
const translatedValidators: { [key: string]: string } = {
  required: 'REQUIRED',
  email: 'EMAIL',
  maxLength: 'MAXLENGTH',
  password: 'PASSWORD', // password validator
  companyType: 'COMPANY_TYPE', // company-type validator
  industrySector: 'INDUSTRY_SECTOR', // industry-sector validator
  emails: 'EMAILS', // multiple emails validator
  enum: 'ENUM', // enum validator
};

export class FormHelper<T extends TForm<T>> {
  private numSubmitted = 0;
  private readonly showErrorsOn: ShowErrorsOn = 'submit';

  constructor(
    private translateService: TranslateService,
    private form: FormGroup<T>,
    showErrorsOn: ShowErrorsOn,
  ) {
    this.showErrorsOn = showErrorsOn;
  }

  trackSubmitted() {
    this.numSubmitted++;
  }

  hasSubmitted() {
    return this.numSubmitted > 0;
  }

  getErrors(field: keyof T): string[] {
    const errors = this.form.get(field as string)?.errors;
    if (this.showErrorsOn === 'submit') {
      return this.hasSubmitted() && errors ? this.translateValidationErrors(errors) : [];
    } else if (this.showErrorsOn === 'touched') {
      return this.form.get(field as string)?.touched && errors ? this.translateValidationErrors(errors) : [];
    }
    return [];
  }

  private translateValidationErrors(errors: ValidationErrors) {
    return Object.keys(errors).map(k => {
      const translationKey = translatedValidators[k] ?? k.toUpperCase();
      return this.translateService.instant(`VALIDATOR.${translationKey}`);
    });
  }
}
